import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import endpoints from '../../api/endpoints';
import api from '../../api/axios';

type Rating = { id: number; text?: string; [key: string]: any };
type IdeaSource = { id: number; label?: string; value?: string; [key: string]: any };

interface RatingSourceState {
  ratings: Rating[];
  ideaSources: IdeaSource[];
  loading: boolean;
  error?: string | null;
}

const initialState: RatingSourceState = {
  ratings: [],
  ideaSources: [],
  loading: false,
  error: null,
};

/**
 * Fetch Ratings and Idea Sources from the backend.
 * Optional `nuid` string will be sent as the `x-nuid` header (matches the curl example).
 */


export const fetchRatingAndSource = createAsyncThunk(
  'ratingSource/fetchRatingAndSource',
 async (
    {
      nuid,
    }: {  nuid: string | null },
    { rejectWithValue}) => {
   try {
      const response = await api.get(endpoints.ubt.testsOfChange.fetchIdeaSourcesRating, {
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      });

      const data = await response.data;

      // Normalize likely shapes returned by the API
      const ratings: Rating[] = data?.ratings ?? data?.ratingList ?? data?.rating ?? [];
      const ideaSources: IdeaSource[] = data?.sources ?? data?.ideaSourceList ?? data?.sourceList ?? data?.ideaSources ?? [];

      return { ratings, ideaSources };
    } catch (err: any) {
      return rejectWithValue(err?.message ?? String(err));
    }
  },
);

const slice = createSlice({
  name: 'ratingSource',
  initialState,
  reducers: {
    clearRatingSourceState(state) {
      state.ratings = [];
      state.ideaSources = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRatingAndSource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRatingAndSource.fulfilled, (state, action: PayloadAction<{ ratings: Rating[]; ideaSources: IdeaSource[] }>) => {
        state.loading = false;
        state.error = null;
        state.ratings = action.payload.ratings ?? [];
        state.ideaSources = action.payload.ideaSources ?? [];
      })
      .addCase(fetchRatingAndSource.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch ratings/sources';
      });
  },
});

export const { clearRatingSourceState } = slice.actions;

export const selectRatings = (state: any) => state.ratingSource?.ratings ?? [];
export const selectIdeaSources = (state: any) => state.ratingSource?.ideaSources ?? [];
export const selectRatingSourceLoading = (state: any) => state.ratingSource?.loading ?? false;
export const selectRatingSourceError = (state: any) => state.ratingSource?.error ?? null;

export default slice.reducer;
